import React, { useEffect } from 'react';
import { ClipboardList, Target, BarChart3, TrendingUp, Users, Award, DollarSign, Briefcase, ArrowRight, CheckCircle, Star, Zap, BookOpen, Headphones } from 'lucide-react';
import { useScrollAnimation, useCountUp } from '../utils/animations';
import { AgentInterestForm } from '../components/AgentInterestForm';

const AnimatedSection = ({ children, animation = 'animate-fade-up', className = '', delay = '' }) => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div ref={ref} className={`${animation} ${isVisible ? 'visible' : ''} ${className}`} style={delay ? { transitionDelay: delay } : {}}>
      {children}
    </div>
  );
};

const StatCounter = ({ end, suffix = '', prefix = '', label }) => {
  const [count, start] = useCountUp(end, 2000);
  const [ref, isVisible] = useScrollAnimation();
  useEffect(() => { if (isVisible) start(); }, [isVisible]);
  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-bold text-brand-blue">{prefix}{count}{suffix}</div>
      <div className="text-sm text-gray-500 mt-2 font-medium">{label}</div>
    </div>
  );
};

const AgentCareers = () => {
  return (
    <div className="min-h-screen" data-testid="agent-careers-page">
      {/* Hero */}
      <section className="relative overflow-hidden grain-overlay" data-testid="careers-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-brand-blue to-blue-900"></div>
        <div className="absolute top-20 right-10 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 container mx-auto px-4 py-6 md:py-7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block px-4 py-1.5 bg-brand-orange/20 text-brand-orange rounded-full text-sm font-semibold mb-6 border border-brand-orange/30">
                Now Recruiting Licensed Agents
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Build Your Insurance <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange to-yellow-400">Business With Purpose</span>
              </h1>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-xl">
                Join Purposefully Covered Insurance Group and get access to cutting-edge tools, training, leads, and a 
                supportive team that helps you close more sales and grow your career.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="#agent-interest"
                  className="bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold text-lg text-center hover:bg-orange-600 transition shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform"
                  data-testid="careers-cta-primary"
                >
                  Get Started
                </a>
                <a
                  href="#how-it-works"
                  className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg text-center hover:bg-white/20 transition border border-white/20"
                >
                  See How It Works
                </a>
              </div>
              <div className="flex items-center gap-6 text-sm text-white/60">
                <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-400" /> No fees to join</span>
                <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-400" /> Training provided</span>
                <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-400" /> 50+ carriers</span>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10 shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_jovial-saha-5/artifacts/jmtbnh10__DSA0690BSC.jpg"
                  alt="David Ashley - Purposefully Covered Insurance Group"
                  className="rounded-xl w-full object-cover object-top"
                  style={{ maxHeight: '500px' }}
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp size={20} className="text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-800">Avg. Agent Earnings</div>
                      <div className="text-xs text-gray-500">Top performers 6-figures+</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-white border-b" data-testid="careers-stats">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCounter end={50} suffix="+" label="Insurance Carriers" />
            <StatCounter end={100} suffix="%" label="Commission Paid" />
            <StatCounter end={24} suffix="/7" label="Agent Support" />
            <StatCounter prefix="$" end={0} suffix="" label="Cost to Join" />
          </div>
        </div>
      </section>

      {/* Agent Portal Features */}
      <section className="py-8 bg-gray-50" data-testid="portal-features">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-14">
            <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">Your Toolkit</span>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mt-2 mb-4">Powerful Agent Portal</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Every agent gets access to our proprietary portal with tools built to help you track, grow, and succeed.</p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: ClipboardList, title: 'Weekly Activity Tracker', desc: 'Log daily activities: leads worked, door knocks, calls, presentations, and sales. Auto-save and submit weekly.', color: 'bg-blue-500' },
              { icon: Target, title: 'Goal Setting & Tracking', desc: 'Set weekly goals for every metric. Compare actuals vs goals with visual reports to stay on target.', color: 'bg-brand-orange' },
              { icon: BarChart3, title: 'Reports & History', desc: 'Full monthly calendar view of all your submitted reports. Print or export any week for your records.', color: 'bg-green-600' },
              { icon: TrendingUp, title: 'Performance Dashboard', desc: 'KPI cards, trend charts, and week-over-week analysis to visualize your growth and identify opportunities.', color: 'bg-purple-600' },
              { icon: Users, title: 'Lead Management', desc: 'Full CRM pipeline to create, assign, track, and convert leads. Add notes, track status, and close more deals.', color: 'bg-teal-600' },
              { icon: Zap, title: 'Automated Reports', desc: 'Reports are automatically emailed to your team lead at scheduled times. No manual sending required.', color: 'bg-amber-600' },
            ].map(({ icon: Icon, title, desc, color }, idx) => (
              <AnimatedSection key={title} animation="animate-fade-up" delay={`${idx * 0.1}s`}>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover-lift h-full">
                  <div className={`${color} text-white w-12 h-12 rounded-xl flex items-center justify-center mb-4`}>
                    <Icon size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-8 bg-white" data-testid="careers-how-it-works">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-14">
            <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">Getting Started</span>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mt-2 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Three simple steps to launch your insurance career</p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: 1, icon: BookOpen, title: 'Apply & Get Approved', desc: 'Fill out a quick application. Our team reviews it and gets you onboarded fast, usually within 24-48 hours.' },
              { step: 2, icon: Headphones, title: 'Get Trained & Equipped', desc: 'Access comprehensive training, scripts, carrier appointments, and your personal Agent Portal with all the tools you need.' },
              { step: 3, icon: DollarSign, title: 'Start Selling & Earning', desc: 'Hit the ground running with leads, support, and a proven system. Earn competitive commissions from day one.' },
            ].map(({ step, icon: Icon, title, desc }, idx) => (
              <AnimatedSection key={step} animation="animate-fade-up" delay={`${idx * 0.2}s`}>
                <div className="text-center relative">
                  {/* Step number */}
                  <div className="relative inline-block mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-brand-blue to-blue-700 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                      <Icon size={36} className="text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-brand-orange text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md">
                      {step}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-brand-blue mb-3">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-8 bg-gray-50" data-testid="what-you-get">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="animate-slide-left">
              <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">The PCG Advantage</span>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mt-2 mb-6">Everything You Need to Succeed</h2>
              <div className="space-y-4">
                {[
                  'Competitive commissions up to 100% on first-year premiums',
                  'Access to 50+ top-rated insurance carriers',
                  'Comprehensive training program for new and experienced agents',
                  'Lead support and proven prospecting methods',
                  'Proprietary Agent Portal with activity tracking and CRM',
                  'Dedicated support team available when you need help',
                  'Team-building opportunities with override income',
                  'Flexible schedule, work from anywhere',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection animation="animate-slide-right">
              <img
                src="https://customer-assets.emergentagent.com/job_jovial-saha-5/artifacts/45yt3963_IMG-8395.jpg"
                alt="David Ashley leading an agent training workshop"
                className="rounded-2xl shadow-xl w-full object-cover"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-8 bg-white" data-testid="agent-testimonials">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-14">
            <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">Agent Stories</span>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mt-2 mb-4">Hear From Our Agents</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Marcus Johnson', role: 'Independent Insurance Agent', quote: "The Agent Portal changed how I manage my business. I can track everything, set goals, and see my progress week by week. It keeps me accountable and motivated." },
              { name: 'Sarah Williams', role: 'Independent Insurance Agent', quote: "The training and support here are unmatched. I came in with zero experience and within 6 months I was consistently hitting my goals. The tools make all the difference." },
              { name: 'David Chen', role: 'Independent Insurance Agent', quote: "What sold me was the lead management system and the automated reporting. I spend less time on admin and more time with clients. My income has doubled since joining." },
            ].map(({ name, role, quote }, idx) => (
              <AnimatedSection key={name} animation="animate-fade-up" delay={`${idx * 0.15}s`}>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 h-full flex flex-col">
                  <div className="flex mb-2">
                    {[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />)}
                  </div>
                  <p className="text-gray-700 italic mb-6 flex-1 leading-relaxed">&ldquo;{quote}&rdquo;</p>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{name}</div>
                    <div className="text-xs text-gray-500">{role}</div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Agent Interest Form */}
      <section id="agent-interest" className="py-8 bg-gray-50" data-testid="agent-interest-section">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-3">Let&apos;s Talk About Your Future</h2>
            <p className="text-base md:text-lg text-gray-600">
              Fill out the quick form below and David Ashley will reach out to schedule a time to talk about joining the team.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <AgentInterestForm />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-8 relative overflow-hidden" data-testid="careers-final-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-blue-700 to-slate-900"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Build Your<br />Insurance Career?
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Join a team that invests in your success. No franchise fees, no desk costs, just the tools, 
              training, and support you need to build a thriving business.
            </p>
            <a
              href="#agent-interest"
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-orange-600 transition shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transform group"
              data-testid="careers-final-cta-btn"
            >
              Get Started <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="text-sm text-white/50 mt-4">Free to apply. No obligations. We&apos;ll reach out within 24 to 48 hours.</p>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default AgentCareers;
